import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Statistic,
  Table,
  Tag,
} from "antd";
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  useGetGraphDataQuery,
  useGetLoanApplicationsQuery,
  useGetTotalPendingDepositsQuery,
  useGetUserDepositsQuery,
  useGetUserWalletsQuery,
} from "store/api";
import { Link, useNavigate } from "react-router-dom";
import { EyeOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectIsFirstTimeLogin } from "store/slices";
import {
  BanknoteArrowUp,
  HandCoins,
  RotateCcwSquare,
  Wallet,
} from "lucide-react";
import { formatCurrency } from "utils";
import { MobileDepositForm } from "components";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const Overview = () => {
  const { data, isLoading } = useGetUserDepositsQuery("deposit");
  const {
    data: wallets,
    isFetching,
    refetch,
  } = useGetUserWalletsQuery("wallets");

  const { data: pendingDeposits, isLoading: isPendingDepositsLoading } =
    useGetTotalPendingDepositsQuery("pendingDeposits");

  const { data: graphData, isLoading: isGraphLoading } =
    useGetGraphDataQuery("graph");

  const { data: loanData, isLoading: isLoanLoading } =
    useGetLoanApplicationsQuery("loan");

  const isFirstTimeLogin = useSelector(selectIsFirstTimeLogin);

  const [open, setOpen] = React.useState(isFirstTimeLogin || false);
  const [depositModal, setDepositModal] = React.useState(false);

  const loans = wallets?.wallets?.find(
    (wallet: any) => wallet?.fields?.type === "loan",
  )?.fields?.amount;
  const savings = wallets?.wallets?.find(
    (wallet: any) => wallet?.fields?.type === "savings",
  )?.fields?.amount;

  const navigate = useNavigate();

  return (
    <div>
      <Row gutter={[14, 16]}>
        <Col xs={24}>
          <Row gutter={[14, 18]}>
            <Col xs={24} md={8}>
              <Card hoverable>
                <Statistic
                  title="Total Savings"
                  value={savings}
                  loading={isFetching}
                  suffix="UGX"
                  formatter={formatCurrency}
                  style={{ fontWeight: 600 }}
                  valueStyle={{
                    fontWeight: 700,
                    color: "green",
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              {" "}
              <Card hoverable>
                <Statistic
                  title="Total Loans"
                  value={loans}
                  suffix="UGX"
                  formatter={formatCurrency}
                  loading={isFetching}
                  style={{ fontWeight: 600 }}
                  valueStyle={{
                    fontWeight: 700,
                    color: "red",
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              {" "}
              <Card hoverable>
                <Statistic
                  title="Pending Approval"
                  value={pendingDeposits?.totalPendingDeposits}
                  loading={isPendingDepositsLoading}
                  suffix="UGX"
                  formatter={formatCurrency}
                  style={{ fontWeight: 600 }}
                  valueStyle={{
                    fontWeight: 700,
                    color: "rgb(234 179 8)",
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={[14, 16]} className="mt-6">
        <Col xs={24} md={24}>
          <div className="flex items-center justify-center flex-wrap gap-6">
            {" "}
            <Card
              bodyStyle={{
                padding: 0,
              }}
              hoverable
              className="border rounded-full max-sm:w-full border-green-500 p-3 hover:text-green-600 hover:bg-[#e6f4ff] text-[16px] font-medium flex items-center gap-6"
              onClick={() => {
                setDepositModal(true);
              }}
            >
              <Wallet className="inline-block mr-2" />
              Make Savings Deposit
            </Card>
            <Card
              bodyStyle={{
                padding: 0,
              }}
              style={
                !loans
                  ? {
                      cursor: "not-allowed",
                      pointerEvents: "none",
                      opacity: 0.5,
                    }
                  : undefined
              }
              hoverable
              className="border  rounded-full border-green-500 max-sm:w-full p-3 hover:text-green-600 hover:bg-[#e6f4ff] text-[16px] font-medium flex items-center gap-6"
              onClick={() => {
                navigate("../apply-for-loan");
              }}
            >
              {" "}
              <BanknoteArrowUp className="inline-block mr-2" />
              Repay Loan
            </Card>{" "}
            <Card
              bodyStyle={{
                padding: 0,
              }}
              hoverable
              style={
                loans
                  ? {
                      cursor: "not-allowed",
                      pointerEvents: "none",
                      opacity: 0.5,
                    }
                  : undefined
              }
              className="border  rounded-full border-red-500 max-sm:w-full p-3 hover:text-red-600 hover:bg-[#e6f4ff] text-[16px] font-medium flex items-center gap-6"
              onClick={() => {
                navigate("../apply-for-loan/?tab=2");
              }}
            >
              <HandCoins className="inline-block mr-2" />
              Request Loan
            </Card>{" "}
            <Card
              bodyStyle={{
                padding: 0,
              }}
              hoverable
              className="border  rounded-full border-green-500 p-3 max-sm:w-full hover:text-green-600 hover:bg-[#e6f4ff] text-[16px] font-medium flex items-center gap-6"
              onClick={() => {
                navigate("../submit-deposit/?type=subscription");
              }}
            >
              <RotateCcwSquare className="inline-block mr-2" />
              Make Subscription
            </Card>
          </div>
        </Col>
      </Row>
      <Row gutter={[14, 16]} className="mt-6">
        <Col xs={24} md={14}>
          <Card
            title="Savings Vs Loans"
            styles={{ title: { color: "#1677ff" } }}
          >
            {
              // @ts-ignore

              isGraphLoading ? (
                <p>Loading...</p>
              ) : (
                graphData && (
                  <Line
                    style={{ height: "240px !important", width: "100%" }}
                    data={{
                      ...graphData,
                    }}
                  />
                )
              )
            }
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card
            title={
              <div className="flex justify-between items-center">
                <span>Recent Transactions</span>
                <Button
                  type="link"
                  className="font-medium underline"
                  onClick={() => {
                    navigate("../transactions");
                  }}
                >
                  View All
                </Button>
              </div>
            }
            styles={{ title: { color: "#1677ff" } }}
          >
            <Table
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Account Number",
                  dataIndex: "accountNumber",
                  key: "accountNumber",
                },
                {
                  title: "Type",
                  dataIndex: "transactionType",
                  key: "transactionType",
                  render: (text) => (
                    <Tag
                      color={text === "savings" ? "green" : "red"}
                      className="capitalize"
                    >
                      {text}
                    </Tag>
                  ),
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (text) => {
                    return (
                      <span>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "UGX",
                        }).format(text)}
                      </span>
                    );
                  },
                },
              ]}
              dataSource={data?.slice(0, 8)}
              loading={isLoading}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[14, 16]} className="mt-6">
        <Col xs={24} md={12}>
          <Card
            title={
              <div className="flex justify-between items-center">
                <span>Upcoming Payments</span>
                <Button
                  type="link"
                  className="underline"
                  onClick={() => {
                    navigate("../transactions");
                  }}
                >
                  View All
                </Button>
              </div>
            }
            styles={{ title: { color: "#1677ff" } }}
          >
            <Table
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Date",
                  dataIndex: "date",
                  key: "date",
                  render: (text) => {
                    return new Date(text).toLocaleDateString();
                  },
                },
                {
                  title: "Type",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Pay Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (text) => {
                    return (
                      <span>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "UGX",
                        }).format(text)}
                      </span>
                    );
                  },
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (text) => {
                    return (
                      <span
                        className={`capitalize ${
                          text === "Pending"
                            ? "text-yellow-500"
                            : "text-green-500"
                        }`}
                      >
                        {text}
                      </span>
                    );
                  },
                },
              ]}
              dataSource={[]}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <div className="flex justify-between items-center">
                <span>Loan Requests</span>
                <Button
                  type="link"
                  className="underline"
                  onClick={() => {
                    navigate("../apply-for-loan");
                  }}
                >
                  View All
                </Button>
              </div>
            }
            styles={{ title: { color: "#1677ff" } }}
          >
            <Table
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Loan Type",
                  dataIndex: "loanType",
                  key: "loanType",
                  render: (text) => {
                    return <span className="capitalize">{text}</span>;
                  },
                },

                {
                  title: "Requested Amount",
                  dataIndex: "loanAmount",
                  key: "loanAmount",
                  render: (text) => {
                    return (
                      <span>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "UGX",
                        }).format(text)}
                      </span>
                    );
                  },
                },
                {
                  title: "Status",
                  dataIndex: "loanStatus",
                  key: "loanStatus",
                  render: (text) => {
                    return (
                      <span
                        className={`capitalize ${
                          text === "Pending"
                            ? "text-yellow-500"
                            : "text-green-500"
                        }`}
                      >
                        {text}
                      </span>
                    );
                  },
                },
                {
                  title: "Actions",
                  render: (text) => {
                    return (
                      <Link to={`/dashboard/loan-request/${text.id}`}>
                        <EyeOutlined className="mr-1" />
                        View
                      </Link>
                    );
                  },
                },
              ]}
              dataSource={loanData?.slice(0, 5)}
              loading={isLoanLoading}
            />
          </Card>
        </Col>
      </Row>
      <Modal
        centered
        title="Update Password"
        loading={false}
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        maskClosable={false}
        footer={null}
        closable={false}
      >
        <Alert
          message="You have logged in with a temporary password, Click button below to update your password"
          type="warning"
          showIcon
        />
        <div className="flex items-center justify-center my-4">
          <Button
            type="primary"
            className="text-center"
            onClick={() =>
              navigate(`../profile`, { state: { modalOpen: true } })
            }
          >
            Update Password
          </Button>
        </div>
      </Modal>
      <Modal
        open={depositModal}
        onCancel={() => setDepositModal(false)}
        centered
        footer={null}
        title="Make a Deposit"
      >
        <MobileDepositForm
          onClose={() => setDepositModal(false)}
          refetch={refetch}
        />
      </Modal>
    </div>
  );
};

export default Overview;
